import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * SecurityGuard component provides additional client-side security
 * This runs on every protected page to ensure authentication is maintained
 */
const SecurityGuard = ({ children }) => {
  const { currentUser, loading } = useAuth();

  useEffect(() => {
    // Additional security check - log unauthorized access attempts
    if (!loading && !currentUser) {
      console.warn('🚨 Security Alert: Unauthorized access attempt detected');
      // In production, you could send this to a logging service
    }
  }, [currentUser, loading]);

  // Optional: Add session timeout warning
  useEffect(() => {
    if (currentUser) {
      const sessionTimeout = 30 * 60 * 1000; // 30 minutes
      const warningTime = 5 * 60 * 1000; // 5 minutes before timeout
      
      let timeoutId;
      let warningId;

      const resetTimer = () => {
        clearTimeout(timeoutId);
        clearTimeout(warningId);
        
        // Set warning timer
        warningId = setTimeout(() => {
          console.warn('Session will expire in 5 minutes');
          // You could show a modal here
        }, sessionTimeout - warningTime);
        
        // Set logout timer
        timeoutId = setTimeout(() => {
          console.warn('🚨 Session expired - logging out');
          // Auto-logout after timeout
          window.location.href = '/login';
        }, sessionTimeout);
      };

      // Reset timer on user activity
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
      events.forEach(event => {
        document.addEventListener(event, resetTimer, true);
      });

      // Initial timer
      resetTimer();

      // Cleanup
      return () => {
        clearTimeout(timeoutId);
        clearTimeout(warningId);
        events.forEach(event => {
          document.removeEventListener(event, resetTimer, true);
        });
      };
    }
  }, [currentUser]);

  return children;
};

export default SecurityGuard;
